import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import {
  buildRouteMapPoints,
  buildRoutePathSegments,
  buildSeaRouteSteps,
  flattenRouteSegments,
} from '@/src/lib/sea-route'
import { routeColors } from '@/src/lib/route-colors'
import type { PublicTrackingContainer, PublicTrackingPosition } from '@/lib/api/types'

/**
 * The shipment's route, drawn from the same interpretation the operator view
 * uses.
 *
 * Route reading comes from sea-route.ts rather than being reimplemented here.
 * A second interpretation is how a customer's map and the internal one end up
 * disagreeing about where the cargo is, which is worse than having no map.
 */
export function PublicRouteMap({
  position,
  containers,
}: {
  position: PublicTrackingPosition | null
  containers: PublicTrackingContainer[]
}) {
  const element = useRef<HTMLDivElement>(null)
  const map = useRef<L.Map | null>(null)

  const steps = buildSeaRouteSteps(
    { route_summary: position?.route_summary ?? null } as never,
    containers as never,
  )
  const points = buildRouteMapPoints(steps, containers as never)

  useEffect(() => {
    if (!element.current || points.length === 0) return

    const instance = L.map(element.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    })
    map.current = instance

    // OpenStreetMap's own tiles need no key. CARTO's basemap CDN watermarks
    // every unauthenticated tile.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(instance)

    const line = flattenRouteSegments(buildRoutePathSegments(points)).map(
      (point) => [point.latitude, point.longitude] as [number, number],
    )
    const colors = routeColors()
    if (line.length > 1) {
      L.polyline(line, { color: colors.planned, weight: 3, opacity: 0.85 }).addTo(instance)
    }

    for (const point of points) {
      L.circleMarker([point.latitude, point.longitude], {
        radius: point.current ? 8 : 6,
        color: point.current ? colors.current : colors.planned,
        fillColor: point.current ? colors.current : '#ffffff',
        fillOpacity: 1,
        weight: 2,
      })
        .addTo(instance)
        .bindTooltip(`${point.mapLabel} · ${point.name}`, { direction: 'top' })
    }

    instance.fitBounds(L.latLngBounds(points.map((point) => [point.latitude, point.longitude])), {
      padding: [40, 40],
      maxZoom: 6,
    })

    return () => {
      instance.remove()
      map.current = null
    }
  }, [points])

  if (points.length === 0) return null

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-medium">Route</h2>
      <div
        ref={element}
        className="h-[320px] w-full overflow-hidden rounded-lg border"
        role="img"
        aria-label="Map of the shipment route"
      />
      {position?.vessel_name && (
        <p className="text-xs text-muted-foreground">
          Vessel {position.vessel_name}
          {position.tracking_updated_at
            ? ` · updated ${new Date(position.tracking_updated_at).toLocaleString()}`
            : ''}
        </p>
      )}
    </section>
  )
}
